import { useState } from "react";
import axios from "axios";

export default function InvoiceDateRangeList() {
  const [search, setSearch] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Direct invoice number search
  const [invoiceNumberSearch, setInvoiceNumberSearch] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Selected invoice for PDF actions
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const token = sessionStorage.getItem("token");

  // =========================================================
  // DATE RANGE SEARCH
  // =========================================================
  const fetchInvoices = async () => {
    if (!fromDate || !toDate) {
      alert("Please select date range");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(
        "https://irc.zentelex.com/api/reports/invoices/monthly-summary",
        {
          params: {
            fromDate,
            toDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setData(result);
      setInvoiceNumberSearch("");
    } catch (error) {
      console.error("Invoice date range error:", error);
      alert("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // INVOICE NUMBER SEARCH
  // =========================================================
  const searchInvoiceNumber = async () => {
    const invoiceNo = invoiceNumberSearch.trim();

    if (!invoiceNo) {
      alert("Please enter Invoice Number");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(
        "https://irc.zentelex.com/api/reports/invoices/monthly-summary",
        {
          params: {
            invoiceNumber: invoiceNo,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setData(result);
      setSearch("");

      if (result.length === 0) {
        alert(`Invoice ${invoiceNo} was not found.`);
      }
    } catch (error) {
      console.error("Invoice number search error:", error);
      alert("Failed to search invoice");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // PDF URL
  // =========================================================
  const getInvoicePdfUrl = (invoiceId) =>
    `https://irc.zentelex.com/api/invoices/${invoiceId}/pdf`;

  // =========================================================
  // VIEW PDF
  // =========================================================
  const viewInvoicePdf = (invoice) => {
    if (!invoice?.id) {
      alert("Invoice ID is missing");
      return;
    }

    window.open(
      getInvoicePdfUrl(invoice.id),
      "_blank",
      "noopener,noreferrer",
    );
  };

  // =========================================================
  // DOWNLOAD PDF
  // =========================================================
  const downloadInvoicePdf = (invoice) => {
    if (!invoice?.id) {
      alert("Invoice ID is missing");
      return;
    }

    const link = document.createElement("a");

    link.href = getInvoicePdfUrl(invoice.id);
    link.download = `${invoice.invoiceNumber || "invoice"}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // =========================================================
  // PRINT PDF
  // =========================================================
  const printInvoicePdf = (invoice) => {
    if (!invoice?.id) {
      alert("Invoice ID is missing");
      return;
    }

    /*
     * Open the saved/generated PDF.
     * The browser PDF viewer provides the Print option.
     */
    const pdfWindow = window.open(
      getInvoicePdfUrl(invoice.id),
      "_blank",
      "noopener,noreferrer",
    );

    if (!pdfWindow) {
      alert("Please allow pop-ups for invoice printing.");
    }
  };

  // =========================================================
  // TABLE SEARCH
  // =========================================================
  const filteredData = data.filter((inv) => {
    const text = search.toLowerCase();

    return (
      inv.invoiceNumber?.toLowerCase().includes(text) ||
      inv.clientName?.toLowerCase().includes(text) ||
      inv.invoiceType?.toLowerCase().includes(text) ||
      inv.godownName?.toLowerCase().includes(text) ||
      inv.invoiceDate?.toLowerCase().includes(text)
    );
  });

  // =========================================================
  // TOTALS
  // =========================================================
  const totalSubtotal = filteredData.reduce(
    (sum, i) => sum + Number(i.subtotal || 0),
    0,
  );

  const totalTax = filteredData.reduce(
    (sum, i) => sum + Number(i.taxAmount || 0),
    0,
  );

  const totalGrand = filteredData.reduce(
    (sum, i) => sum + Number(i.grandTotal || 0),
    0,
  );

  return (
    <div className="container-fluid py-4">

      {/* =====================================================
          HEADER
          ===================================================== */}
      <div className="card shadow-lg border-0 mb-4 bg-primary text-white">
        <div className="card-body">
          <h4 className="fw-bold mb-1">
            🧾 Invoice Report
          </h4>

          <p className="mb-0 opacity-75">
            Search, view, download and print saved invoices
          </p>
        </div>
      </div>

      {/* =====================================================
          DATE RANGE SEARCH
          ===================================================== */}
      <div className="card shadow-sm border-0 mb-4 bg-light">
        <div className="card-body">

          <div className="row g-3 align-items-end">

            <div className="col-md-4">
              <label className="form-label fw-semibold text-secondary">
                From Date
              </label>

              <input
                type="date"
                className="form-control border-primary"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold text-secondary">
                To Date
              </label>

              <input
                type="date"
                className="form-control border-primary"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-primary w-100 fw-semibold shadow-sm"
                onClick={fetchInvoices}
                disabled={loading}
              >
                {loading ? "⏳ Loading..." : "📊 Show Invoices"}
              </button>
            </div>

          </div>

        </div>
      </div>

      {/* =====================================================
          DIRECT INVOICE NUMBER SEARCH
          ===================================================== */}
      <div className="card shadow-sm border-0 mb-4 bg-light">
        <div className="card-body">

          <div className="row g-3 align-items-end">

            <div className="col-md-9">

              <label className="form-label fw-semibold text-secondary">
                Invoice Number
              </label>

              <input
                type="text"
                className="form-control border-primary"
                placeholder="Enter Invoice Number e.g. INV/2026-27/0011"
                value={invoiceNumberSearch}
                onChange={(e) =>
                  setInvoiceNumberSearch(e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchInvoiceNumber();
                  }
                }}
              />

            </div>

            <div className="col-md-3">

              <button
                type="button"
                className="btn btn-success w-100 fw-semibold shadow-sm"
                onClick={searchInvoiceNumber}
                disabled={loading}
              >
                🔎 Search Invoice
              </button>

            </div>

          </div>

        </div>
      </div>

      {/* =====================================================
          TABLE FILTER
          ===================================================== */}
      <div className="card shadow-sm border-0 mb-4">

        <div className="card-body">

          <input
            type="text"
            className="form-control border-primary"
            placeholder="🔍 Search Invoice / Client / Type / Godown..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

        </div>

      </div>

      {/* =====================================================
          INVOICE TABLE
          ===================================================== */}
      <div className="card shadow-sm border-0">

        <div className="card-body p-0">

          <div className="table-responsive">

            <table className="table table-hover align-middle mb-0">

              <thead className="table-primary text-center">

                <tr>
                  <th>Date</th>
                  <th>Invoice No</th>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Godown</th>
                  <th>Subtotal (₹)</th>
                  <th>Tax (₹)</th>
                  <th>Grand Total (₹)</th>
                </tr>

              </thead>

              <tbody>

                {!loading && filteredData.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center text-muted py-4"
                    >
                      No invoices found
                    </td>
                  </tr>
                )}

                {filteredData.map((inv) => (

                  <tr key={inv.id}>

                    <td className="text-center">
                      {inv.invoiceDate}
                    </td>

                    {/* =================================================
                        CLICKABLE INVOICE NUMBER
                        ================================================= */}
                    <td className="text-center">

                      <button
                        type="button"
                        className="btn btn-link p-0 fw-bold text-primary"
                        title="View invoice PDF / Download / Print"
                        onClick={() => setSelectedInvoice(inv)}
                      >
                        {inv.invoiceNumber}
                      </button>

                    </td>

                    <td className="fw-semibold">
                      {inv.clientName}
                    </td>

                    <td className="text-center">

                      <span className="badge bg-info text-dark">
                        {inv.invoiceType}
                      </span>

                    </td>

                    <td className="text-center">
                      {inv.godownName}
                    </td>

                    <td className="text-end text-success fw-semibold">
                      {Number(inv.subtotal || 0).toFixed(2)}
                    </td>

                    <td className="text-end text-warning fw-semibold">
                      {Number(inv.taxAmount || 0).toFixed(2)}
                    </td>

                    <td className="text-end fw-bold text-primary">
                      {Number(inv.grandTotal || 0).toFixed(2)}
                    </td>

                  </tr>

                ))}

              </tbody>

              {/* =====================================================
                  TOTALS
                  ===================================================== */}
              {filteredData.length > 0 && (

                <tfoot className="table-dark text-end fw-bold">

                  <tr>

                    <td colSpan="5">
                      TOTAL
                    </td>

                    <td className="text-success">
                      {totalSubtotal.toFixed(2)}
                    </td>

                    <td className="text-warning">
                      {totalTax.toFixed(2)}
                    </td>

                    <td className="text-info">
                      {totalGrand.toFixed(2)}
                    </td>

                  </tr>

                </tfoot>

              )}

            </table>

          </div>

        </div>

      </div>

      {/* =====================================================
          INVOICE ACTION MODAL
          ===================================================== */}
      {selectedInvoice && (

        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 1055,
          }}
          onClick={() => setSelectedInvoice(null)}
        >

          <div
            className="card shadow-lg border-0"
            style={{
              width: "430px",
              maxWidth: "92%",
            }}
            onClick={(e) => e.stopPropagation()}
          >

            <div className="card-header bg-primary text-white">

              <h5 className="mb-0 fw-bold">
                🧾 Invoice Actions
              </h5>

            </div>

            <div className="card-body">

              <div className="mb-3">

                <div className="text-muted small">
                  Invoice Number
                </div>

                <div className="fw-bold fs-5 text-primary">
                  {selectedInvoice.invoiceNumber}
                </div>

              </div>

              <div className="mb-3">

                <div className="text-muted small">
                  Client
                </div>

                <div className="fw-semibold">
                  {selectedInvoice.clientName || "-"}
                </div>

              </div>

              <div className="mb-3">

                <div className="text-muted small">
                  Invoice Date
                </div>

                <div className="fw-semibold">
                  {selectedInvoice.invoiceDate || "-"}
                </div>

              </div>

              <div className="mb-4">

                <div className="text-muted small">
                  Grand Total
                </div>

                <div className="fw-bold fs-5 text-success">
                  ₹ {Number(
                    selectedInvoice.grandTotal || 0
                  ).toFixed(2)}
                </div>

              </div>

              <div className="d-grid gap-2">

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() =>
                    viewInvoicePdf(selectedInvoice)
                  }
                >
                  👁️ View PDF
                </button>

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() =>
                    downloadInvoicePdf(selectedInvoice)
                  }
                >
                  ⬇️ Download PDF
                </button>

                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() =>
                    printInvoicePdf(selectedInvoice)
                  }
                >
                  🖨️ Open PDF for Print
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    setSelectedInvoice(null)
                  }
                >
                  ✖ Close
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}
